/**
 * Phira's bezier cache key (F§6.7, R§2.1), computed in f32 like Phira:
 *
 *   q(v)  = round(fround(fround(v) * 100)) as i16        (f32 multiply; Rust `f32::round` = half away from zero)
 *   k0    = (q(x1) * 100 + q(y1)) as u16                 (i16 multiply/add wrap, then reinterpreted as u16)
 *   key   = (k0, q(x2), q(y2))                            returned as the string "k0,k1,k2"
 *
 * Goldens: 0.145 -> 15 (f32 14.5; f64 would give 14), 0.285 -> 29, 0.565 -> 57; x1 > 3.27 overflows the i16
 * multiply and wraps (6.56 behaves like 0.64 in the first slot). Collisions are real: (0.01, 0.00) and
 * (0.00, 1.00) both give k0 = 100.
 *
 * Note on `as i16`: a Rust float-to-int cast SATURATES (NaN -> 0) rather than wrapping, so q(v) is clamped to
 * [-32768, 32767]; this only differs from a wrapping conversion for |v| > 327.67. The i16 arithmetic on the
 * quantised values wraps (release build).
 */

import type { BezierPoints } from './types';

const fr = Math.fround;

/** Rust `f32::round` / `f64::round`: halves round away from zero. */
export function roundHalfAwayFromZero(v: number): number {
  if (!Number.isFinite(v)) return v;
  return v < 0 ? -Math.round(-v) : Math.round(v);
}

/** Two's-complement wrap to i16. */
export function wrapI16(n: number): number {
  return (n << 16) >> 16;
}

/** Two's-complement wrap to u16. */
export function wrapU16(n: number): number {
  return n & 0xffff;
}

/** Rust `float as i16` (saturating, NaN -> 0). */
export function saturateI16(v: number): number {
  if (Number.isNaN(v)) return 0;
  if (v <= -32768) return -32768;
  if (v >= 32767) return 32767;
  return Math.trunc(v);
}

/** q(v): the 0.01-quantised control value Phira keys bezier curves by. */
export function quantizeBezierCoord(v: number): number {
  return saturateI16(roundHalfAwayFromZero(fr(fr(v) * 100)));
}

/** The three key components (k0 as u16, k1 and k2 as i16). */
export function bezierKeyParts(p: BezierPoints): readonly [number, number, number] {
  const qx1 = quantizeBezierCoord(p[0]);
  const qy1 = quantizeBezierCoord(p[1]);
  const k0 = wrapU16(wrapI16(wrapI16(qx1 * 100) + qy1));
  return [k0, quantizeBezierCoord(p[2]), quantizeBezierCoord(p[3])];
}

/** Phira's bezier cache key as the string "k0,k1,k2". */
export function bezierKey(p: BezierPoints): string {
  const [k0, k1, k2] = bezierKeyParts(p);
  return `${k0},${k1},${k2}`;
}
